/**
 * Typed HTTP client for the two backend surfaces the browser may call:
 *
 *   /admin/v1  Admin API (staff-only; Registrar Control Center business/compliance/registry views)
 *   /ops/v1    Observability API (apps/api/observability; fronts Prometheus, Loki and Tempo)
 *
 * Both are same-origin paths (proxied by Vite in dev, by the edge in deployed
 * environments). Absolute URLs are rejected, so a caller cannot point the
 * browser at a metrics, logs or traces backend directly.
 *
 * Every request carries `X-Request-Id`. Every write (POST/PUT/PATCH/DELETE)
 * carries `Idempotency-Key`. Failures are thrown as `ApiError`, normalised to the
 * stable envelope `{ error: { code, message, request_id, details } }`.
 */

import { newIdempotencyKey, newRequestId } from '../lib/ids';
import { getOpsReason } from './opsReason';
import { clearSession, getToken } from './session';
import type {
  AbuseCase,
  AbuseCaseDetail,
  AbuseCaseFilter,
  AbuseTransitionRequest,
  AlertmanagerAlert,
  ApprovalDecisionRequest,
  ApprovalPage,
  ApprovalQueueQuery,
  ApprovalRequest,
  ApprovalStatusChange,
  AuditPage,
  AuditQuery,
  CallOptions,
  Correlation,
  DisclosureCase,
  DisclosureCaseSummary,
  DisclosureDecisionRequest,
  DisclosureListQuery,
  DisclosureReleaseResult,
  DisclosureStatusChange,
  DemoLogin,
  Deployment,
  Dispute,
  DisputeCloseRequest,
  DisputeDecisionRequest,
  DisputeFilter,
  DomainAction,
  DomainActionRequest,
  DomainDetail,
  DomainLinks,
  DomainListQuery,
  DomainListResponse,
  DomainTimeline,
  Incident,
  IncidentDetail,
  IncidentTransitionRequest,
  ItemList,
  LawEnforcementStatus,
  LawEnforcementUpdate,
  LoginRequest,
  LoginResponse,
  LogRetention,
  LogRetentionUpdate,
  LogsQueryRequest,
  LogsResponse,
  Me,
  MetricsInstantRequest,
  MetricsRangeRequest,
  NewAbuseReport,
  NewApproval,
  NewDisclosureRequest,
  NewDispute,
  NewEvidence,
  NewIncident,
  NewLawEnforcementRequest,
  OpenedAbuseCase,
  OpenedDisclosureCase,
  OpsOverview,
  OpsQueues,
  OpsRegistry,
  OpsService,
  PolicyDecision,
  PromQueryData,
  RegistrarApi,
  RegistryExchanges,
  RegistryTransaction,
  RegistryTransactionDetail,
  RevealResponse,
  SearchResponse,
  Slo,
  SubjectDetail,
  TempoSearchResponse,
  TempoTrace,
  TimelineQuery,
  TraceSearchRequest,
  VnnicCoverage,
  VnProvince,
  VnWard,
  VnPlacesStatus,
  VnnicBusinessLookup,
  VerificationRegistryLookup,
  VnnicException,
  VnnicExceptionQuery,
  VnnicRegistryList,
  VnnicRegistryListCheck,
  VnnicRegistryListQuery,
  VnnicExportResponse,
  VnnicGenerateReportRequest,
  VnnicGenerateReportResponse,
  VnnicReportBatch,
  VnnicReportBatchDetail,
  VnnicReportOutcomeRequest,
  VnnicReportQuery,
  VnnicReportStatus,
  VnnicResubmitResponse,
  VnnicStatusChange,
  VnnicSyncOutcomeRequest,
  VnnicSyncQuery,
  VnnicSyncRow,
  VnnicSyncTransaction,
  VnnicTransactionStatus,
  WriteOptions,
} from './types';
import { accountsHttp } from './areas/accounts.http';
import { adminHttp } from './areas/admin.http';
import { coreHttp } from './areas/core.http';
import { governanceHttp } from './areas/governance.http';
import { integrationsHttp } from './areas/integrations.http';
import { lookupHttp } from './areas/lookup.http';
import { commerceHttp } from './areas/commerce.http';
import { registryHttp } from './areas/registry.http';
import { marketHttp } from './areas/market.http';
import { notifyHttp } from './areas/notify.http';
import { modulesHttp } from './areas/modules.http';
import { addonsHttp } from './areas/addons.http';
import { znsHttp } from './areas/zns.http';
import { catalogHttp } from './areas/catalog.http';
import { subscriptionsHttp } from './areas/subscriptions.http';
import { einvoiceHttp } from './areas/einvoice.http';
import { operatorHttp } from './areas/operator.http';
import { webhooksHttp } from './areas/webhooks.http';
import { reportingHttp } from './areas/reporting.http';
import { nameChangeHttp } from './areas/namechange.http';
import { servicesHttp } from './areas/services.http';
import { workHttp } from './areas/work.http';
import { declarationsHttp } from './areas/declarations.http';
import { countsHttp } from './areas/counts.http';
import { hostingHttp } from './areas/hosting.http';
import { dnsHttp } from './areas/dns.http';

// Surfaces per ADR-013 / ADR-014 and master spec §98: the Control Center talks to the
// staff-only admin API (never the customer `/api/v1`) and to the Observability API.
export const API_BASE = '/admin/v1';
export const OPS_BASE = '/ops/v1';

export type ApiSurface = 'api' | 'ops';

const BASE_PATHS: Readonly<Record<ApiSurface, string>> = { api: API_BASE, ops: OPS_BASE };

// ---------------------------------------------------------------------------
// Error model

/** The stable error envelope every backend endpoint returns on failure. */
export interface ApiErrorBody {
  error: {
    code: string;
    message: string;
    request_id: string;
    details?: unknown;
  };
}

/** Codes produced by this client when the server never sent a usable envelope. */
export type ClientErrorCode = 'NETWORK_ERROR' | 'INVALID_ENDPOINT' | 'UNEXPECTED_RESPONSE';

export interface ApiErrorInit {
  code: string;
  message: string;
  requestId: string;
  /** HTTP status; 0 when no response was received. */
  status: number;
  details?: unknown;
  cause?: unknown;
}

export class ApiError extends Error {
  override readonly name = 'ApiError';
  readonly code: string;
  readonly requestId: string;
  readonly status: number;
  readonly details: unknown;

  constructor(init: ApiErrorInit) {
    super(init.message, init.cause === undefined ? undefined : { cause: init.cause });
    this.code = init.code;
    this.requestId = init.requestId;
    this.status = init.status;
    this.details = init.details;
  }

  toBody(): ApiErrorBody {
    return {
      error: { code: this.code, message: this.message, request_id: this.requestId, details: this.details },
    };
  }
}

export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError;
}

export function isApiErrorBody(value: unknown): value is ApiErrorBody {
  if (typeof value !== 'object' || value === null || !('error' in value)) return false;
  const err: unknown = value.error;
  return (
    typeof err === 'object' &&
    err !== null &&
    'code' in err &&
    typeof err.code === 'string' &&
    'message' in err &&
    typeof err.message === 'string'
  );
}

export function isAbortError(value: unknown): boolean {
  return value instanceof DOMException && value.name === 'AbortError';
}

// ---------------------------------------------------------------------------
// Transport

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
const WRITE_METHODS: ReadonlySet<HttpMethod> = new Set<HttpMethod>(['POST', 'PUT', 'PATCH', 'DELETE']);

export type QueryParams = Record<string, string | number | boolean | null | undefined>;

export interface RequestOptions {
  query?: QueryParams;
  body?: unknown;
  signal?: AbortSignal;
  /** Writes only. Generated when omitted; pass the same key to retry the same operation. */
  idempotencyKey?: string;
  /** Writes only: `false` sends `notify_customer: false` ("Gửi hook cho khách" unticked). */
  notifyCustomer?: boolean;
  headers?: Record<string, string>;
}

function buildUrl(surface: ApiSurface, path: string, query: QueryParams | undefined, requestId: string): string {
  // Relative, rooted paths only: no scheme, no protocol-relative `//host`, no `..` escapes.
  if (!path.startsWith('/') || path.startsWith('//') || /^[a-z][a-z\d+.-]*:/i.test(path) || path.includes('..')) {
    throw new ApiError({
      code: 'INVALID_ENDPOINT' satisfies ClientErrorCode,
      message: `Refusing to call "${path}": only paths under ${BASE_PATHS[surface]} are allowed`,
      requestId,
      status: 0,
    });
  }
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== undefined && value !== null) params.append(key, String(value));
  }
  const qs = params.toString();
  return `${BASE_PATHS[surface]}${path}${qs ? `?${qs}` : ''}`;
}

async function readJson(res: Response): Promise<unknown> {
  const text = await res.text();
  if (!text) return undefined;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

export async function request<T>(
  surface: ApiSurface,
  method: HttpMethod,
  path: string,
  opts: RequestOptions = {},
): Promise<T> {
  const requestId = newRequestId();
  // "Gửi hook cho khách" unticked: a JSON object body carries `notify_customer: false`, any
  // other write carries it as a query parameter (the admin API reads both).
  const quiet = WRITE_METHODS.has(method) && opts.notifyCustomer === false;
  const objectBody = opts.body !== null && typeof opts.body === 'object' && !Array.isArray(opts.body);
  const sentBody = quiet && objectBody ? { ...(opts.body as Record<string, unknown>), notify_customer: false } : opts.body;
  const query = quiet && !objectBody ? { ...opts.query, notify_customer: false } : opts.query;
  const url = buildUrl(surface, path, query, requestId);

  const headers = new Headers(opts.headers);
  headers.set('Accept', 'application/json');
  headers.set('X-Request-Id', requestId);
  if (WRITE_METHODS.has(method)) headers.set('Idempotency-Key', opts.idempotencyKey ?? newIdempotencyKey());
  // One staff bearer token authenticates both surfaces.
  const token = getToken();
  if (token) headers.set('Authorization', `Bearer ${token}`);
  // Observability queries naming business objects need a stated reason (spec §122).
  const reason = surface === 'ops' ? getOpsReason() : null;
  if (reason) headers.set('X-Ops-Reason', reason);

  let body: string | undefined;
  if (sentBody !== undefined) {
    headers.set('Content-Type', 'application/json');
    body = JSON.stringify(sentBody);
  }

  let res: Response;
  try {
    res = await fetch(url, { method, headers, body, signal: opts.signal, credentials: 'same-origin' });
  } catch (cause) {
    if (isAbortError(cause)) throw cause;
    throw new ApiError({
      code: 'NETWORK_ERROR' satisfies ClientErrorCode,
      message: 'The server could not be reached.',
      requestId,
      status: 0,
      cause,
    });
  }

  const payload = await readJson(res);
  const effectiveRequestId = res.headers.get('X-Request-Id') ?? requestId;

  if (!res.ok) {
    // A rejected token ends the session everywhere; the auth gate then shows the login page.
    if (res.status === 401 && token) clearSession('expired');
    if (isApiErrorBody(payload)) {
      throw new ApiError({
        code: payload.error.code,
        message: payload.error.message,
        requestId: payload.error.request_id || effectiveRequestId,
        status: res.status,
        details: payload.error.details,
      });
    }
    // Not our envelope (e.g. an HTML 502 from a proxy): synthesise one.
    throw new ApiError({
      code: `HTTP_${res.status}`,
      message: res.statusText || `Request failed with status ${res.status}`,
      requestId: effectiveRequestId,
      status: res.status,
      details: typeof payload === 'string' ? payload.slice(0, 500) : payload,
    });
  }

  if (res.status !== 204 && payload !== undefined && typeof payload !== 'object') {
    throw new ApiError({
      code: 'UNEXPECTED_RESPONSE' satisfies ClientErrorCode,
      message: 'The server returned a non-JSON response.',
      requestId: effectiveRequestId,
      status: res.status,
    });
  }
  return payload as T;
}

/**
 * A binary GET (PDF / image) with the staff token; failures use the same envelope as JSON calls.
 * Used for declaration files, which are read with a reason and audited server-side.
 */
export async function requestBlob(surface: ApiSurface, path: string, query?: QueryParams, signal?: AbortSignal): Promise<Blob> {
  const requestId = newRequestId();
  const url = buildUrl(surface, path, query, requestId);
  const headers = new Headers({ 'X-Request-Id': requestId });
  const token = getToken();
  if (token) headers.set('Authorization', `Bearer ${token}`);
  let res: Response;
  try {
    res = await fetch(url, { method: 'GET', headers, signal, credentials: 'same-origin' });
  } catch (cause) {
    if (isAbortError(cause)) throw cause;
    throw new ApiError({ code: 'NETWORK_ERROR' satisfies ClientErrorCode, message: 'The server could not be reached.', requestId, status: 0, cause });
  }
  if (!res.ok) {
    if (res.status === 401 && token) clearSession('expired');
    const payload = await readJson(res);
    if (isApiErrorBody(payload)) {
      throw new ApiError({ code: payload.error.code, message: payload.error.message, requestId: payload.error.request_id || requestId, status: res.status, details: payload.error.details });
    }
    throw new ApiError({ code: `HTTP_${res.status}`, message: res.statusText || `Request failed with status ${res.status}`, requestId, status: res.status });
  }
  return res.blob();
}

export const http = {
  get: <T>(surface: ApiSurface, path: string, opts?: Omit<RequestOptions, 'body' | 'idempotencyKey'>) =>
    request<T>(surface, 'GET', path, opts),
  post: <T>(surface: ApiSurface, path: string, body: unknown, opts?: Omit<RequestOptions, 'body'>) =>
    request<T>(surface, 'POST', path, { ...opts, body }),
  put: <T>(surface: ApiSurface, path: string, body: unknown, opts?: Omit<RequestOptions, 'body'>) =>
    request<T>(surface, 'PUT', path, { ...opts, body }),
  patch: <T>(surface: ApiSurface, path: string, body: unknown, opts?: Omit<RequestOptions, 'body'>) =>
    request<T>(surface, 'PATCH', path, { ...opts, body }),
  delete: <T>(surface: ApiSurface, path: string, opts?: Omit<RequestOptions, 'body'>) =>
    request<T>(surface, 'DELETE', path, opts),
};


// ---------------------------------------------------------------------------
// Endpoint bindings. Paths mirror apps/api/admin/src/routes/mod.rs and
// subsystems/platform/facade/adapters/observability-api/src/routes.rs.

const seg = encodeURIComponent;
const rd = (o?: CallOptions) => ({ signal: o?.signal });
export const wr = (o?: WriteOptions) => ({ signal: o?.signal, idempotencyKey: o?.idempotencyKey, notifyCustomer: o?.notifyCustomer });

export const httpApi: RegistrarApi = {
  ...lookupHttp,
  ...countsHttp,
  ...coreHttp,
  ...governanceHttp,
  ...integrationsHttp,
  ...adminHttp,
  ...accountsHttp,
  ...commerceHttp,
  ...registryHttp,
  ...marketHttp,
  ...notifyHttp,
  ...modulesHttp,
  ...addonsHttp,
  ...znsHttp,
  ...catalogHttp,
  ...subscriptionsHttp,
  ...einvoiceHttp,
  ...operatorHttp,
  ...workHttp,
  ...declarationsHttp,
  ...webhooksHttp,
  ...reportingHttp,
  ...servicesHttp,
  ...nameChangeHttp,
  ...hostingHttp,
  ...dnsHttp,

  // Session. Login is the only call made without a token.
  login: (req: LoginRequest, opts?: CallOptions) => http.post<LoginResponse>('api', '/auth/login', req, rd(opts)),
  demoLoginStatus: (opts?: CallOptions) => http.get<DemoLogin>('api', '/auth/demo-login', rd(opts)),
  demoLogin: (opts?: CallOptions) => http.post<LoginResponse>('api', '/auth/demo-login', {}, rd(opts)),
  logout: (opts?: WriteOptions) => http.post<{ ok: boolean }>('api', '/auth/logout', {}, wr(opts)),
  me: (opts?: CallOptions) => http.get<Me>('api', '/me', rd(opts)),
  deployment: (opts?: CallOptions) => http.get<Deployment>('api', '/deployment', rd(opts)),

  // Domains and registry
  listDomains: (q: DomainListQuery, opts?: CallOptions) =>
    http.get<DomainListResponse>('api', '/domains', {
      // State filters go as comma-separated lists; an empty list is no filter.
      query: { ...q, lifecycle_state: q.lifecycle_state?.join(',') || undefined, dossier_state: q.dossier_state?.join(',') || undefined, compliance_state: q.compliance_state?.join(',') || undefined },
      ...rd(opts),
    }),
  getDomain: (key: string, opts?: CallOptions) => http.get<DomainDetail>('api', `/domains/${seg(key)}`, rd(opts)),
  getDomainTimeline: (key: string, q: TimelineQuery, opts?: CallOptions) =>
    http.get<DomainTimeline>('api', `/domains/${seg(key)}/timeline`, { query: { before: q.before, limit: q.limit }, ...rd(opts) }),
  listDomainRegistryTransactions: (key: string, opts?: CallOptions) =>
    http.get<ItemList<RegistryTransaction>>('api', `/domains/${seg(key)}/registry-transactions`, rd(opts)),
  listDomainComplianceDecisions: (key: string, opts?: CallOptions) =>
    http.get<ItemList<PolicyDecision>>('api', `/domains/${seg(key)}/compliance-decisions`, rd(opts)),
  getRegistryTransaction: (id: string, opts?: CallOptions) =>
    http.get<RegistryTransactionDetail>('api', `/registry/transactions/${seg(id)}`, rd(opts)),
  search: (query: string, opts?: CallOptions) => http.get<SearchResponse>('api', '/search', { query: { q: query }, ...rd(opts) }),

  // Subjects. The reveal endpoint is the audit point: who, what, when and why are
  // recorded before any cleartext is returned.
  getSubject: (id: string, opts?: CallOptions) => http.get<SubjectDetail>('api', `/subjects/${seg(id)}`, rd(opts)),
  revealSubjectIdentifier: (identifierId: string, reason: string, opts?: WriteOptions) =>
    http.post<RevealResponse>('api', `/subjects/identifiers/${seg(identifierId)}/reveal`, { reason }, wr(opts)),
  revealSubjectBirthDate: (subjectId: string, reason: string, opts?: WriteOptions) =>
    http.post<RevealResponse>('api', `/subjects/${seg(subjectId)}/birth-date/reveal`, { reason }, wr(opts)),

  // Approvals and audit
  listApprovals: (opts?: CallOptions) => http.get<ItemList<ApprovalRequest>>('api', '/approvals', rd(opts)),
  listApprovalQueue: (q: ApprovalQueueQuery, opts?: CallOptions) =>
    http.get<ApprovalPage>('api', '/approvals', { query: { status: q.status, action: q.action, before: q.before, limit: q.limit }, ...rd(opts) }),
  createApproval: (req: NewApproval, opts?: WriteOptions) => http.post<ApprovalStatusChange>('api', '/approvals', req, wr(opts)),
  decideApproval: (id: string, req: ApprovalDecisionRequest, opts?: WriteOptions) =>
    http.post<ApprovalStatusChange>('api', `/approvals/${seg(id)}/decision`, req, wr(opts)),
  listAudit: (q: AuditQuery, opts?: CallOptions) =>
    http.get<AuditPage>('api', '/audit', {
      query: {
        resource_type: q.resource_type,
        resource_id: q.resource_id,
        actor_id: q.actor_id,
        actor_type: q.actor_type,
        action: q.action,
        outcome: q.outcome,
        correlation_id: q.correlation_id,
        before: q.before,
        limit: q.limit,
      },
      ...rd(opts),
    }),

  // Abuse cases
  listAbuseCases: (f: AbuseCaseFilter, opts?: CallOptions) =>
    http.get<ItemList<AbuseCase>>('api', '/abuse/cases', {
      query: { status: f.status, category: f.category, domain: f.domain, overdue: f.overdue || undefined, limit: f.limit },
      ...rd(opts),
    }),
  getAbuseCase: (id: string, opts?: CallOptions) => http.get<AbuseCaseDetail>('api', `/abuse/cases/${seg(id)}`, rd(opts)),
  createAbuseCase: (req: NewAbuseReport, opts?: WriteOptions) => http.post<OpenedAbuseCase>('api', '/abuse/cases', req, wr(opts)),
  transitionAbuseCase: (id: string, req: AbuseTransitionRequest, opts?: WriteOptions) =>
    http.post<AbuseCase>('api', `/abuse/cases/${seg(id)}/transition`, req, wr(opts)),
  addAbuseEvidence: (caseId: string, req: NewEvidence, opts?: WriteOptions) =>
    http.post<{ id: string; case_id: string }>('api', `/abuse/cases/${seg(caseId)}/evidence`, req, wr(opts)),
  addLawEnforcementRequest: (caseId: string, req: NewLawEnforcementRequest, opts?: WriteOptions) =>
    http.post<{ id: string; case_id: string; status: 'RECEIVED' }>('api', `/abuse/cases/${seg(caseId)}/law-enforcement-requests`, req, wr(opts)),
  updateLawEnforcementRequest: (id: string, req: LawEnforcementUpdate, opts?: WriteOptions) =>
    http.post<{ id: string; status: LawEnforcementStatus }>('api', `/abuse/law-enforcement-requests/${seg(id)}`, req, wr(opts)),
  // The Idempotency-Key is the action's business key end to end, registry command included.
  requestDomainAction: (caseId: string, req: DomainActionRequest, opts?: WriteOptions) =>
    http.post<DomainAction>('api', `/abuse/cases/${seg(caseId)}/domain-actions`, req, wr(opts)),

  // Disputes
  listDisputes: (f: DisputeFilter, opts?: CallOptions) =>
    http.get<ItemList<Dispute>>('api', '/disputes', { query: { status: f.status, domain_id: f.domain_id, limit: f.limit }, ...rd(opts) }),
  getDispute: (id: string, opts?: CallOptions) => http.get<Dispute>('api', `/disputes/${seg(id)}`, rd(opts)),
  openDispute: (req: NewDispute, opts?: WriteOptions) => http.post<Dispute>('api', '/disputes', req, wr(opts)),
  decideDispute: (id: string, req: DisputeDecisionRequest, opts?: WriteOptions) =>
    http.post<Dispute>('api', `/disputes/${seg(id)}/decision`, req, wr(opts)),
  markDisputeImplemented: (id: string, note: string, opts?: WriteOptions) =>
    http.post<Dispute>('api', `/disputes/${seg(id)}/implemented`, { note }, wr(opts)),
  closeDispute: (id: string, req: DisputeCloseRequest, opts?: WriteOptions) =>
    http.post<Dispute>('api', `/disputes/${seg(id)}/close`, req, wr(opts)),

  // Registration-data disclosure
  listDisclosureCases: (q: DisclosureListQuery, opts?: CallOptions) =>
    http.get<ItemList<DisclosureCaseSummary>>('api', '/disclosure-cases', { query: { status: q.status, limit: q.limit }, ...rd(opts) }),
  getDisclosureCase: (id: string, opts?: CallOptions) => http.get<DisclosureCase>('api', `/disclosure-cases/${seg(id)}`, rd(opts)),
  createDisclosureCase: (req: NewDisclosureRequest, opts?: WriteOptions) =>
    http.post<OpenedDisclosureCase>('api', '/disclosure-cases', req, wr(opts)),
  startDisclosureReview: (id: string, opts?: WriteOptions) =>
    http.post<DisclosureStatusChange>('api', `/disclosure-cases/${seg(id)}/review`, {}, wr(opts)),
  requestDisclosureInfo: (id: string, what: string, opts?: WriteOptions) =>
    http.post<DisclosureStatusChange>('api', `/disclosure-cases/${seg(id)}/request-info`, { request: what }, wr(opts)),
  decideDisclosureCase: (id: string, req: DisclosureDecisionRequest, opts?: WriteOptions) =>
    http.post<DisclosureStatusChange>('api', `/disclosure-cases/${seg(id)}/decision`, req, wr(opts)),
  // Returns the approved data ONCE. Callers must not cache or persist it.
  releaseDisclosureCase: (id: string, channel: string, opts?: WriteOptions) =>
    http.post<DisclosureReleaseResult>('api', `/disclosure-cases/${seg(id)}/release`, { channel }, wr(opts)),
  closeDisclosureCase: (id: string, reason: string, opts?: WriteOptions) =>
    http.post<DisclosureStatusChange>('api', `/disclosure-cases/${seg(id)}/close`, { reason }, wr(opts)),

  // Observability API
  getOpsOverview: (opts?: CallOptions) => http.get<OpsOverview>('ops', '/overview', rd(opts)),
  listOpsServices: (opts?: CallOptions) => http.get<ItemList<OpsService>>('ops', '/services', rd(opts)),
  listOpsRegistries: (opts?: CallOptions) => http.get<ItemList<OpsRegistry>>('ops', '/registries', rd(opts)),
  getOpsQueues: (opts?: CallOptions) => http.get<OpsQueues>('ops', '/queues', rd(opts)),
  listAlerts: (opts?: CallOptions) => http.get<ItemList<AlertmanagerAlert>>('ops', '/alerts', rd(opts)),
  queryMetrics: (req: MetricsInstantRequest, opts?: CallOptions) => http.post<PromQueryData>('ops', '/metrics/query', req, rd(opts)),
  queryMetricsRange: (req: MetricsRangeRequest, opts?: CallOptions) =>
    http.post<PromQueryData>('ops', '/metrics/query-range', req, rd(opts)),
  queryLogs: (req: LogsQueryRequest, opts?: CallOptions) => http.post<LogsResponse>('ops', '/logs/query', req, rd(opts)),
  tailLogs: (query: string, sinceSeconds: number, opts?: CallOptions) =>
    http.get<LogsResponse>('ops', '/logs/tail', { query: { query, since_seconds: sinceSeconds }, ...rd(opts) }),
  getTrace: (traceId: string, opts?: CallOptions) => http.get<TempoTrace>('ops', `/traces/${seg(traceId)}`, rd(opts)),
  searchTraces: (req: TraceSearchRequest, opts?: CallOptions) => http.post<TempoSearchResponse>('ops', '/traces/search', req, rd(opts)),
  correlate: (id: string, opts?: CallOptions) => http.get<Correlation>('ops', `/correlate/${seg(id)}`, rd(opts)),
  getRegistryExchanges: (cltrid: string, opts?: CallOptions) =>
    http.get<RegistryExchanges>('ops', `/registry-exchanges/${seg(cltrid)}`, rd(opts)),
  getDomainLinks: (name: string, opts?: CallOptions) => http.get<DomainLinks>('ops', `/domains/${seg(name)}/links`, rd(opts)),
  listIncidents: (opts?: CallOptions) => http.get<ItemList<Incident>>('ops', '/incidents', rd(opts)),
  getIncident: (id: string, opts?: CallOptions) => http.get<IncidentDetail>('ops', `/incidents/${seg(id)}`, rd(opts)),
  openIncident: (req: NewIncident, opts?: WriteOptions) => http.post<Incident>('ops', '/incidents', req, wr(opts)),
  transitionIncident: (id: string, req: IncidentTransitionRequest, opts?: WriteOptions) =>
    http.post<Incident>('ops', `/incidents/${seg(id)}/transition`, req, wr(opts)),
  listSlos: (opts?: CallOptions) => http.get<ItemList<Slo>>('ops', '/slo', rd(opts)),
  getLogRetention: (opts?: CallOptions) => http.get<LogRetention>('ops', '/settings/log-retention', rd(opts)),
  updateLogRetention: (req: LogRetentionUpdate, opts?: WriteOptions) =>
    http.put<LogRetention>('ops', '/settings/log-retention', req, wr(opts)),

  // Reporting to the authorities (VNNIC channel). Reads need `regulatory_report.read`; writes need
  // `regulatory_report.manage`; business lookups `business_registry.lookup`; the diagnostic promotion
  // code `promo_code.request`. All audited server-side (ADR-021).
  getVnnicCoverage: (opts?: CallOptions) => http.get<VnnicCoverage>('api', '/vnnic/coverage', rd(opts)),
  listVnProvinces: (opts?: CallOptions) => http.get<ItemList<VnProvince>>('api', '/reference/vn-places/provinces', rd(opts)),
  listVnWards: (provinceId: number, opts?: CallOptions) =>
    http.get<ItemList<VnWard>>('api', `/reference/vn-places/provinces/${provinceId}/wards`, rd(opts)),
  getVnPlacesStatus: (opts?: CallOptions) => http.get<VnPlacesStatus>('api', '/reference/vn-places/status', rd(opts)),
  syncVnPlaces: (opts?: CallOptions) => http.post<{ sync_id: string; provinces: number; wards: number; retired: number }>('api', '/reference/vn-places/sync', {}, rd(opts)),
  lookupVnnicEnterprise: (gdtCode: string, opts?: CallOptions) => http.get<VnnicBusinessLookup>('api', `/vnnic/enterprises/${seg(gdtCode)}`, rd(opts)),
  lookupVnnicHousehold: (taxCode: string, opts?: CallOptions) => http.get<VnnicBusinessLookup>('api', `/vnnic/business-households/${seg(taxCode)}`, rd(opts)),
  lookupVerificationRegistry: (verificationId: string, opts?: WriteOptions) =>
    http.post<VerificationRegistryLookup>('api', `/vnnic/verifications/${seg(verificationId)}/registry-lookup`, {}, wr(opts)),
  listVnnicExceptions: (query: VnnicExceptionQuery, opts?: CallOptions) =>
    http.get<ItemList<VnnicException>>('api', '/vnnic/exceptions', { query: { gap: query.gap, limit: query.limit }, ...rd(opts) }),
  getVnnicRegistryList: (query: VnnicRegistryListQuery, opts?: CallOptions) =>
    http.get<VnnicRegistryList>('api', '/vnnic/registry-list', { query: { kind: query.kind, limit: query.limit }, ...rd(opts) }),
  checkVnnicRegistryList: (opts?: WriteOptions) =>
    http.post<{ status: VnnicRegistryListCheck['status']; check: VnnicRegistryListCheck }>('api', '/vnnic/registry-list/check', {}, wr(opts)),
  listVnnicSync: (query: VnnicSyncQuery, opts?: CallOptions) =>
    http.get<ItemList<VnnicSyncRow>>('api', '/vnnic/sync', { query: { status: query.status, limit: query.limit }, ...rd(opts) }),
  getVnnicSyncTransaction: (id: string, opts?: CallOptions) =>
    http.get<VnnicSyncTransaction>('api', `/vnnic/sync-transactions/${seg(id)}`, rd(opts)),
  recordVnnicSyncOutcome: (id: string, req: VnnicSyncOutcomeRequest, opts?: WriteOptions) =>
    http.post<VnnicStatusChange<VnnicTransactionStatus>>('api', `/vnnic/sync-transactions/${seg(id)}/outcome`, req, wr(opts)),
  resubmitVnnicSync: (dossierId: string, reason: string, opts?: WriteOptions) =>
    http.post<VnnicResubmitResponse>('api', `/vnnic/sync/${seg(dossierId)}/resubmit`, { reason }, wr(opts)),
  listVnnicReports: (query: VnnicReportQuery, opts?: CallOptions) =>
    http.get<ItemList<VnnicReportBatch>>('api', '/vnnic/reports', { query: { report_type: query.report_type, limit: query.limit }, ...rd(opts) }),
  getVnnicReport: (id: string, opts?: CallOptions) => http.get<VnnicReportBatchDetail>('api', `/vnnic/reports/${seg(id)}`, rd(opts)),
  generateVnnicReport: (req: VnnicGenerateReportRequest, opts?: WriteOptions) =>
    http.post<VnnicGenerateReportResponse>('api', '/vnnic/reports', req, wr(opts)),
  exportVnnicReport: (id: string, opts?: WriteOptions) =>
    http.post<VnnicExportResponse>('api', `/vnnic/reports/${seg(id)}/export`, undefined, wr(opts)),
  recordVnnicReportOutcome: (id: string, req: VnnicReportOutcomeRequest, opts?: WriteOptions) =>
    http.post<VnnicStatusChange<VnnicReportStatus>>('api', `/vnnic/reports/${seg(id)}/outcome`, req, wr(opts)),
};
